import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  Shield, Users, Lock, Bot, Database, Eye, ChevronRight,
  CheckCircle, Activity, Server, FileText, BarChart3, Globe, Loader2
} from 'lucide-react';
import axios from 'axios';
import ERMAComplianceTab from '@/components/ERMAComplianceTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem('mosaic_session_token');
const authH = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

/* ═══ Guided Walkthrough Steps ═══ */
const WALKTHROUGH_STEPS = [
  {
    id: 'welcome',
    icon: Shield,
    title: 'Welcome to the ERMA Security Review',
    desc: 'This guided walkthrough demonstrates how MOSAIC handles student data privacy, security controls, and automated processing transparency. All data shown is sample data from a demo school — no real student information is displayed.',
    color: 'bg-blue-100 text-blue-700',
  },
  {
    id: 'rbac',
    icon: Lock,
    title: 'Role-Based Access Control (RBAC)',
    desc: 'MOSAIC enforces 5 access levels: Student, Teacher, School Admin, District Admin, and Super Admin. Each role can only see data within their scope. Teachers see only their classes. Schools see only their students. Your "erma_reviewer" account is a restricted role that cannot access financial or business data.',
    color: 'bg-red-100 text-red-700',
  },
  {
    id: 'data-isolation',
    icon: Database,
    title: 'School-Level Data Isolation',
    desc: 'Every database query is filtered by school_id and district_id. A teacher at School A cannot see students at School B — even if they try to manipulate the API. The demo school "ERMA Demo School" is completely isolated from all real school data.',
    color: 'bg-indigo-100 text-indigo-700',
  },
  {
    id: 'sample-students',
    icon: Users,
    title: 'Sample Student Data',
    desc: 'Below you\'ll see 5 sample students with WIDA proficiency scores. These are fictional students created for this review. Real student data is never exposed to reviewers. Notice the scores use the WIDA 1.0-6.0 scale across Listening, Speaking, Reading, and Writing.',
    color: 'bg-emerald-100 text-emerald-700',
  },
  {
    id: 'automated-transparency',
    icon: Bot,
    title: 'Automated Data Flow Transparency',
    desc: 'MOSAIC uses automated content-scoring services for 8 features (Speaking Lab, Writing Portfolio, etc.). Student names and IDs are NEVER sent to the MOSAIC content-scoring vendor — only anonymized response text. the MOSAIC content-scoring vendor\'s API terms prohibit model training on this data. The Automated Processing Disclosure section in the compliance tab maps every feature.',
    color: 'bg-purple-100 text-purple-700',
  },
  {
    id: 'audit-logging',
    icon: Eye,
    title: 'Audit Logging & Monitoring',
    desc: 'All authentication events, data access, and admin actions are logged with timestamps and user IDs. The Live Compliance Monitoring dashboard shows real-time metrics: active sessions, failed logins, lockouts, Automated API calls, and breach reports.',
    color: 'bg-amber-100 text-amber-700',
  },
  {
    id: 'compliance-docs',
    icon: FileText,
    title: 'Compliance Documentation',
    desc: 'The ERMA Compliance Center tab contains all required artifacts: Data Elements Inventory, Storage Architecture, Security Controls, Automated Processing Disclosure, Retention/Deletion Policy, Subprocessor List, Incident Response Plan, Parent Bill of Rights, DPA Attachments A-E, and Chancellor\'s Reg A-820 alignment. Plus SOC 2 readiness and data flow diagrams that exceed ERMA minimums.',
    color: 'bg-green-100 text-green-700',
  },
];

const ERMAReviewerDashboard = () => {
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [showDocs, setShowDocs] = useState(false);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSample = async () => {
      try {
        const res = await axios.get(`${API}/compliance/erma-sample-data`, authH());
        setStudents(res.data.students || []);
      } catch { }
      setLoading(false);
    };
    fetchSample();
  }, []);

  const currentStep = WALKTHROUGH_STEPS[step];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ERMA Review Mode Banner */}
      <div className="bg-amber-500 text-black py-2 px-4 text-center sticky top-0 z-50" data-testid="erma-review-banner">
        <div className="flex items-center justify-center gap-2">
          <Shield size={16} />
          <span className="font-bold text-sm">ERMA REVIEW MODE</span>
          <span className="text-xs bg-white/30 px-2 py-0.5 rounded">Sample Data Only — No Real Student Information</span>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-bold text-gray-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
              MOSAIC — ERMA Security Review
            </h1>
            <p className="text-xs text-gray-500">
              Logged in as: {user?.email} | Role: ERMA Reviewer | School: ERMA Demo School (Sample Data)
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowDocs(false)}
              className={`px-4 py-2 rounded-lg text-xs font-bold transition-colors ${!showDocs ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}
              data-testid="erma-walkthrough-btn"
            >
              Guided Walkthrough
            </button>
            <button
              onClick={() => setShowDocs(true)}
              className={`px-4 py-2 rounded-lg text-xs font-bold transition-colors ${showDocs ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}
              data-testid="erma-compliance-docs-btn"
            >
              Compliance Documents
            </button>
          </div>
        </div>

        {showDocs ? (
          <ERMAComplianceTab />
        ) : (
          <div className="space-y-5">
            {/* Step Progress */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-1 mb-3">
                {WALKTHROUGH_STEPS.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => setStep(i)}
                    className={`flex-1 h-2 rounded-full transition-colors ${i <= step ? 'bg-blue-600' : 'bg-gray-200'}`}
                    data-testid={`erma-step-${i}`}
                  />
                ))}
              </div>
              <p className="text-[10px] text-gray-400 text-center">Step {step + 1} of {WALKTHROUGH_STEPS.length}</p>
            </div>

            {/* Current Step Card */}
            <div className="bg-white border-2 border-blue-200 rounded-lg p-6" data-testid="erma-current-step">
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${currentStep.color}`}>
                  <currentStep.icon size={24} />
                </div>
                <div className="flex-1">
                  <h2 className="text-base font-bold text-gray-800 mb-2">{currentStep.title}</h2>
                  <p className="text-sm text-gray-600 leading-relaxed">{currentStep.desc}</p>
                </div>
              </div>
              <div className="flex justify-between mt-4 pt-3 border-t border-gray-100">
                <button
                  onClick={() => setStep(Math.max(0, step - 1))}
                  disabled={step === 0}
                  className="text-xs text-gray-500 hover:text-blue-600 disabled:opacity-30"
                >
                  Previous
                </button>
                <button
                  onClick={() => setStep(Math.min(WALKTHROUGH_STEPS.length - 1, step + 1))}
                  disabled={step === WALKTHROUGH_STEPS.length - 1}
                  className="flex items-center gap-1 text-xs font-bold text-blue-600 hover:text-blue-700 disabled:opacity-30"
                >
                  Next <ChevronRight size={14} />
                </button>
              </div>
            </div>

            {/* Sample Student Data (visible at step 3+) */}
            {step >= 3 && (
              <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="erma-sample-students">
                <div className="flex items-center gap-2 mb-3">
                  <Users size={18} className="text-emerald-600" />
                  <h3 className="font-bold text-gray-800 text-sm">Sample Student Data — ERMA Demo School</h3>
                  <span className="text-[9px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded font-bold">SAMPLE DATA</span>
                </div>
                {loading ? (
                  <div className="text-center py-6"><Loader2 className="animate-spin mx-auto text-gray-400" size={20} /></div>
                ) : (
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="text-left p-3 font-bold text-gray-600">Student</th>
                          <th className="text-left p-3 font-bold text-gray-600">Grade</th>
                          <th className="text-center p-3 font-bold text-blue-600">Listening</th>
                          <th className="text-center p-3 font-bold text-green-600">Speaking</th>
                          <th className="text-center p-3 font-bold text-amber-600">Reading</th>
                          <th className="text-center p-3 font-bold text-red-600">Writing</th>
                          <th className="text-center p-3 font-bold text-gray-800">Composite</th>
                        </tr>
                      </thead>
                      <tbody>
                        {students.map((s, i) => (
                          <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                            <td className="p-3 font-medium">{s.name}</td>
                            <td className="p-3 text-gray-500">{s.grade}</td>
                            <td className="p-3 text-center font-bold text-blue-600">{s.scores?.listening || '-'}</td>
                            <td className="p-3 text-center font-bold text-green-600">{s.scores?.speaking || '-'}</td>
                            <td className="p-3 text-center font-bold text-amber-600">{s.scores?.reading || '-'}</td>
                            <td className="p-3 text-center font-bold text-red-600">{s.scores?.writing || '-'}</td>
                            <td className="p-3 text-center font-bold">{s.composite || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <p className="text-[10px] text-gray-400 mt-2">These are fictional students. WIDA scores use the 1.0-6.0 proficiency scale. Real student data is only visible to authorized teachers and administrators within their school.</p>
              </div>
            )}

            {/* Security Controls Demo (visible at step 1+) */}
            {step >= 1 && (
              <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="erma-security-demo">
                <div className="flex items-center gap-2 mb-3">
                  <Lock size={18} className="text-red-600" />
                  <h3 className="font-bold text-gray-800 text-sm">Live Security Controls</h3>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { label: 'Your Role', value: 'erma_reviewer', desc: 'Restricted — no financial data access', color: 'text-amber-600' },
                    { label: 'Data Scope', value: 'Demo School Only', desc: 'Cannot see real school data', color: 'text-blue-600' },
                    { label: 'Session Expiry', value: '24 hours', desc: 'Auto-logout after expiry', color: 'text-green-600' },
                    { label: 'Password', value: 'bcrypt (cost 12)', desc: 'One-way hash, never stored plain', color: 'text-indigo-600' },
                    { label: 'Connection', value: 'TLS 1.2+', desc: 'All traffic encrypted', color: 'text-purple-600' },
                    { label: 'Financial Data', value: 'BLOCKED', desc: 'Quotes, commissions, sales hidden', color: 'text-red-600' },
                  ].map((c, i) => (
                    <div key={i} className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                      <div className="text-[10px] text-gray-500 uppercase font-bold">{c.label}</div>
                      <div className={`text-sm font-bold ${c.color}`}>{c.value}</div>
                      <div className="text-[10px] text-gray-400">{c.desc}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* What's Hidden from Reviewer */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-4" data-testid="erma-hidden-data">
              <h4 className="text-xs font-bold text-red-800 mb-2">Data NOT Accessible to ERMA Reviewers</h4>
              <div className="grid grid-cols-4 gap-2">
                {['Quotes & Pricing', 'Commission Tracker', 'Sales Pipeline', 'Leads Database', 'Revenue Data', 'Beta Agreements', 'A/B Testing', 'Real Student Data'].map((item, i) => (
                  <div key={i} className="flex items-center gap-1.5 text-[11px] text-red-700">
                    <Lock size={10} className="text-red-400" /> {item}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ERMAReviewerDashboard;
